import { FileExtended } from "../classes/fileExtended";

const rgbaToHex = require('hex-and-rgba').rgbaToHex;

interface RGBA {
  r: number;
  g: number;
  b: number;
  a: number;
}

interface Pixel {
  x: number;
  y: number;
  rgba: RGBA;
  hex: any;
}

export class MosaicService {
  Jimp: any;

  constructor() {
    this.Jimp = (window as any).Jimp;
  }

  getPixels(image: any): Pixel[][] {
    var pixels = [];

    for (let x = 0; x < image.bitmap.width; x++) {
      var newRow: Pixel[] = []

      for (let y = 0; y < image.bitmap.height; y++) {
        const pixelColor = image.getPixelColor(x, y);
        const rgba = this.Jimp.intToRGBA(pixelColor);

        newRow.push({
          x: x,
          y: y,
          rgba: rgba,
          hex: rgbaToHex(rgba.r, rgba.g, rgba.b)
        });
      }
      pixels.push(newRow);
    }
    return pixels;
  }

  getPixelsAverageColour(pixels: Pixel[]): RGBA {
    const totalColour = pixels.reduce((acc, pixel) => {
      acc.r += pixel.rgba.r;
      acc.g += pixel.rgba.g;
      acc.b += pixel.rgba.b;
      acc.a += pixel.rgba.a;
      return acc;
    }, { r: 0, g: 0, b: 0, a: 0 });

    return {
      r: Math.round(totalColour.r / pixels.length),
      g: Math.round(totalColour.g / pixels.length),
      b: Math.round(totalColour.b / pixels.length),
      a: Math.round(totalColour.a / pixels.length)
    };
  }

  getImageBlocks(pixels: any[][], blockSizeWidth: number, blockSizeHeight: number): Pixel[][] {
    const blocks = [];

    for (let x = 0; x < pixels.length; x += blockSizeWidth) {
      const newRow = [];

      for (let y = 0; y < pixels[x].length; y += blockSizeHeight) {
        const newRgba = this.getPixelsAverageColour(pixels[x].slice(y, y + blockSizeHeight));
        const block = {
          x: x,
          y: y,
          rgba: newRgba,
          hex: rgbaToHex(newRgba.r, newRgba.g, newRgba.b, 0.4)
        };
        newRow.push(block);
      }
      blocks.push(newRow);
    }
    return blocks;
  }

  async getSampleImages(files: FileExtended[]) {
    const imagePromises = files.map(file => {
      return this.Jimp.read(file.fileUrl);
    });

    const images = await Promise.all(imagePromises);
    const averageImageWidth = images.map(image => image.bitmap.width).reduce((acc, width) => acc + width, 0) / images.length;
    const averageImageHeight = images.map(image => image.bitmap.height).reduce((acc, height) => acc + height, 0) / images.length;

    const resizedImagePromises = images.map((image) => {
      return image.resize(averageImageWidth, averageImageHeight);
    });
    return await Promise.all(resizedImagePromises);
  }

  async getImageDatas(images: FileExtended[], width: number, height: number) {
    const resizedImagePromises = images.map((image: any) => {
      return image.resize(width, height);
    });

    const resizedImages = await Promise.all(resizedImagePromises);

    return resizedImages.map((image) => {
      return {
        data: new ImageData(
          Uint8ClampedArray.from(image.bitmap.data),
          image.bitmap.width,
          image.bitmap.height
        ),
        averageColour: this.getPixelsAverageColour(this.getPixels(image).flat(1))
      }
    });
  }

  getColourDiff(rgb1: any, rgb2: any) {
    return Math.sqrt(
      Math.pow(rgb1.r - rgb2.r, 2) +
      Math.pow(rgb1.g - rgb2.g, 2) +
      Math.pow(rgb1.b - rgb2.b, 2)
    );
  }

  createCanvas(width: number, height: number): HTMLCanvasElement {
    let canvas = document.getElementById('render-canvas') as HTMLCanvasElement;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.setAttribute("id", "render-canvas");
    }
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }

  async createMosaic(mainImageFile: FileExtended, sampleImageFiles: FileExtended[], sampleSize: number, adjustColorVal: number, randomizeImages: boolean): Promise<string> {
    const targetImage = await this.Jimp.read(mainImageFile.fileUrl);
    const sampleImages = await this.getSampleImages(sampleImageFiles);

    const sampleImageAspectRatio = sampleImages[0].bitmap.width / sampleImages[0].bitmap.height;

    const blockSizeWidth = Math.floor(targetImage.bitmap.width / sampleSize);
    const blockSizeHeight = Math.floor(blockSizeWidth / sampleImageAspectRatio);

    const originalPixels = this.getPixels(targetImage);
    const pixels = this.getImageBlocks(originalPixels, blockSizeWidth, blockSizeHeight);

    const width = targetImage.bitmap.width;
    const height = targetImage.bitmap.height;

    const canvas = this.createCanvas(width, height);
    const context = canvas.getContext('2d') as CanvasRenderingContext2D;

    const imageDatas = await this.getImageDatas(sampleImages, blockSizeWidth, blockSizeHeight);

    for (let x = 0; x < pixels.length; x++) {
      for (let y = 0; y < pixels[x].length; y++) {
        const pixel = pixels[x][y];
        let imageData = imageDatas[Math.floor(Math.random() * imageDatas.length)];

        if (!randomizeImages) {
          imageData = imageDatas.sort((a, b) => {
            return this.getColourDiff(a.averageColour, pixel.rgba) > this.getColourDiff(b.averageColour, pixel.rgba) ? 1 : -1;
          })[0];
        }

        context.putImageData(imageData.data, pixel.x, pixel.y);
      }
    }

    if (adjustColorVal > 0) {
      const imageLoadPromise = new Promise<HTMLImageElement>(resolve => {
        const image = new Image();
        image.src = URL.createObjectURL(mainImageFile.file);
        image.onload = () => { resolve(image); };
      });

      const image = await imageLoadPromise;
      context.globalAlpha = adjustColorVal;
      context.drawImage(image, 0, 0);   

      image.parentNode?.removeChild(image);
    }
    return canvas.toDataURL();
  }
}